import {
  FLOOD_SCRUBBER_ALLOWED_HOURS,
  FLOOD_TIMELINE_DATA_MONTH_RANGE,
} from './floodTimelineCalendar';

/** Cửa sổ (giờ) mà đường bao cường độ bão demo áp dụng lên scrub — kết thúc tại `now`. */
export const DEMO_STORM_WINDOW_HOURS = FLOOD_SCRUBBER_ALLOWED_HOURS;

/** Số **giờ** trước `now` tại đó đỉnh bão mô phỏng. */
const PEAK_HOURS_BEFORE_NOW = 8;
const ENVELOPE_FLOOR = 0.35;

/**
 * `true` khi bão demo cần **thay** cường độ theo mùa nền (tháng thực tại ngoài mùa mưa 6–11).
 * Giữ đơn nguồn sự thật: chỉ dựa `nowMs` (năm thực), không mix với `analysisYear` (năm kịch bản GEE).
 */
export function shouldOverrideSeasonal(nowMs: number): boolean {
  const d = new Date(nowMs);
  if (Number.isNaN(d.getTime())) return true;
  const m = d.getMonth() + 1;
  return m < FLOOD_TIMELINE_DATA_MONTH_RANGE.start || m > FLOOD_TIMELINE_DATA_MONTH_RANGE.end;
}

/**
 * Cường độ 0..1 theo số **giờ** trước `now` (0 = tại bây giờ, 72 = đầu cửa sổ).
 * Đỉnh ở `PEAK_HOURS_BEFORE_NOW` giờ. Đầu cửa sổ ~`ENVELOPE_FLOOR`, tại 0h ~0,5.
 */
export function getStormProfileHoursBeforeNow(tHours: number, windowH: number): number {
  if (tHours < 0 || tHours > windowH) {
    return 0.35;
  }
  if (tHours <= PEAK_HOURS_BEFORE_NOW) {
    return 0.5 + 0.5 * (tHours / Math.max(PEAK_HOURS_BEFORE_NOW, 1e-6));
  }
  const span = windowH - PEAK_HOURS_BEFORE_NOW;
  if (span <= 0) {
    return 1;
  }
  return 1 - (1 - ENVELOPE_FLOOR) * ((tHours - PEAK_HOURS_BEFORE_NOW) / span);
}

export function isInDemoStormWindow(iso: string, nowMs: number): boolean {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t) || !Number.isFinite(nowMs)) return false;
  const start = nowMs - DEMO_STORM_WINDOW_HOURS * 3600000;
  return t >= start && t <= nowMs;
}

/**
 * Tích hợp với hệ số cơ sở từ `floodDemoTimeIntensityFactors`. `stormScale` để tách nhân riêng nếu cần.
 * Ngoài mùa mưa thực tế: bão demo **thay** mùa (đảm bảo có ngập để demo). Trong mùa mưa: **cộng**
 * thêm tầng bão nổi rõ đỉnh ~8h trước now (phân biệt với background).
 */
export function getDemoStormScales(
  iso: string | null | undefined,
  nowMs: number,
  base: { hourScale: number; seasonalScale: number },
): { hourScale: number; seasonalScale: number; stormScale: number; inWindow: boolean } {
  if (!iso || !isInDemoStormWindow(iso, nowMs)) {
    return {
      hourScale: base.hourScale,
      seasonalScale: base.seasonalScale,
      stormScale: 1,
      inWindow: false,
    };
  }

  const tIso = new Date(iso).getTime();
  const tHours = (nowMs - tIso) / 3600000;
  const p = getStormProfileHoursBeforeNow(tHours, FLOOD_SCRUBBER_ALLOWED_HOURS);
  const override = shouldOverrideSeasonal(nowMs);
  if (override) {
    return {
      hourScale: base.hourScale * (0.88 + 0.12 * p),
      seasonalScale: 0.5 + 0.8 * p,
      stormScale: 0.9 + 0.55 * p,
      inWindow: true,
    };
  }
  return {
    hourScale: base.hourScale * (0.92 + 0.08 * p),
    seasonalScale: base.seasonalScale * (0.7 + 0.35 * p),
    stormScale: 0.85 + 0.6 * p,
    inWindow: true,
  };
}
